<template>
	<div>
		<!-- Content section -->
		<div v-if="contentOnly" class="connection-info-container">
			<div
				class="connection-info-item"
				v-for="item in connectionInfoItems"
				:key="item.id || item.label"
			>
				<div class="info-list-label">{{ item.label }}:</div>
				<div class="info-list-value">
					{{ item.value }}
				</div>
			</div>
		</div>

		<!-- Sidebar section -->
		<div v-if="sidebarOnly">
			<SidebarSection>
				<InfoList
					:items="settingsItems"
					:title="$t('admin_integrations_settings')"
				>
					<!-- Custom label slot - only render for rows that carry a control -->
					<template #label="{ item }">
						<div
							v-if="item.type === 'toggle' || item.type === 'stepper'"
							class="label-tip"
						>
							<label>{{ item.label }}</label>
							<Tippy
								v-if="item.tooltip"
								arrow
								theme="large"
								:content="item.tooltip"
								:options="{ animation: 'scale' }"
							>
								<CircleQuestionIcon regular class="help-icon" />
							</Tippy>
						</div>
					</template>

					<!-- Custom value slot -->
					<template #value="{ item }">
						<ToggleSwitch
							v-if="item.type === 'toggle'"
							v-model="item.value"
							:disabled="isSavingConfiguration"
							@change="handleSettingChange(item.key, $event)"
							class="settings-toggle-switch"
						/>
						<NumberStepper
							v-else-if="item.type === 'stepper'"
							:model-value="item.value"
							:min="REMOVAL_DAYS_MIN"
							:max="REMOVAL_DAYS_MAX"
							:disabled="isSavingConfiguration"
							@commit="handleSettingChange(item.key, $event)"
							class="settings-stepper"
						/>
						<!-- For link items, show the button spanning the full width -->
						<Button
							v-else-if="item.type === 'link'"
							link
							:href="item.url"
							:to="item.route"
							:target="item.target"
							class="settings-link full-width"
						>
							{{ item.linkText }}
							<ArrowUpRightFromSquareIcon type="solid" />
						</Button>
						<!-- Fallback for other item types -->
						<span v-else>{{ item.value || '-' }}</span>
					</template>
				</InfoList>
			</SidebarSection>

			<SidebarSection v-if="showActionsSection">
				<InfoList
					:items="actionsInfoItems"
					:title="t('admin_groups_actions_label')"
				>
					<!-- Same slot/classes as the Settings labels so colors match -->
					<template #label="{ item }">
						<div class="label-tip">
							<label>{{ item.label }}</label>
						</div>
					</template>

					<template #value="{ item }">
						<Button
							v-if="item.key === 'role_mapping'"
							link
							class="role-mapping-button"
							@click="emit('editRoleMappings')"
						>
							<PlusIcon v-if="!hasRoleMappings" type="regular" />
							{{
								hasRoleMappings
									? t('admin_integrations_edit')
									: t('common_add_button_title')
							}}
						</Button>
						<Button
							v-else
							link
							class="sync-all-button"
							:disabled="!fullSyncAvailable || isSyncingAll"
							@click="emit('execFullSync')"
						>
							<span v-if="!isSyncingAll">{{
								t('admin_integrations_sync_all')
							}}</span>
							<ArrowsRotateIcon type="regular" :spin="isSyncingAll" />
						</Button>
					</template>
				</InfoList>
				<div
					v-if="
						showFullSync &&
						!fullSyncAvailable &&
						configuration?.full_sync_available_at
					"
					class="sync-available-pill"
					:title="
						dateFormat(
							configuration.full_sync_available_at,
							'MM/DD/YYYY, hh:mm A'
						)
					"
				>
					<span>{{ t('admin_integrations_sync_available') }}</span>
					<strong>{{
						dateFormat(configuration.full_sync_available_at, 'M/D/YY, h:mm A')
					}}</strong>
				</div>
			</SidebarSection>

			<SidebarSection>
				<ActionList
					localize
					:items="actionItems"
					:title="showActionsSection ? null : 'admin_groups_actions_label'"
					@select="onSelectAction"
				/>
			</SidebarSection>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Button, NumberStepper, useAlert, Tippy } from '@/components/common'
import moment from 'moment'
import { dateFormat } from '@/helperMethods'
import {
	TrashCanIcon,
	CircleQuestionIcon,
	ArrowUpRightFromSquareIcon,
	ArrowsRotateIcon,
	PlusIcon,
} from '@/icons'
import ToggleSwitch from '@/components/common/ToggleSwitch'
import { InfoList, ActionList, SidebarSection } from '../../common'
import { getDocumentationUrl } from '../documentation'

// Props
const props = defineProps({
	// eslint-disable-next-line vue/no-unused-properties -- reserved for the max group size row (currently commented out)
	connection: {
		type: Object,
		required: false,
		default: null,
	},
	configuration: {
		type: Object,
		required: false,
		default: null,
	},
	platformConfiguration: {
		type: Object,
		required: false,
		default: null,
	},
	contentOnly: {
		type: Boolean,
		default: false,
	},
	sidebarOnly: {
		type: Boolean,
		default: false,
	},
	// Disables the toggles while any configure PATCH is in flight, so an
	// overlapping change can't send stale flag values
	isSavingConfiguration: {
		type: Boolean,
		default: false,
	},
	isSyncingAll: {
		type: Boolean,
		default: false,
	},
})

// Matches the server's smallint column on remove_group_days_after_course_end.
// Negative days remove the group before the course end date.
const REMOVAL_DAYS_MIN = -32768
const REMOVAL_DAYS_MAX = 32767

// Reactive State
const actionItems = [
	{
		id: 'delete',
		icon: TrashCanIcon,
		type: 'danger',
		label: 'admin_integrations_delete_connection',
	},
]

const settingsItems = computed(() => [
	{
		key: 'sync_enabled',
		label: t('admin_integrations_sync_enabled'),
		tooltip: t('admin_integrations_sync_enabled_desc'),
		type: 'toggle',
		value: props.configuration?.sync_enabled ?? false,
	},
	{
		key: 'launch_enabled',
		label: t('admin_integrations_launch_enabled'),
		tooltip: t('admin_integrations_launch_enabled_desc'),
		type: 'toggle',
		value: props.configuration?.launch_enabled ?? false,
	},
	...(showGroupRemoval.value
		? [
				{
					key: 'remove_group_on_course_end',
					label: t('admin_integrations_remove_group_on_course_end'),
					tooltip: t('admin_integrations_remove_group_on_course_end_desc'),
					type: 'toggle',
					value: props.configuration?.remove_group_on_course_end ?? false,
				},
				// The days only mean anything while removal is on, so the row is
				// hidden rather than disabled when the flag is off
				...(props.configuration?.remove_group_on_course_end
					? [
							{
								key: 'remove_group_days_after_course_end',
								label: t(
									'admin_integrations_remove_group_days_after_course_end'
								),
								tooltip: t(
									'admin_integrations_remove_group_days_after_course_end_desc'
								),
								type: 'stepper',
								value:
									props.configuration?.remove_group_days_after_course_end ?? 0,
							},
					  ]
					: []),
		  ]
		: []),
	// Hidden rather than linked to another platform's article when this
	// platform has no walkthrough yet
	...(documentationUrl.value
		? [
				{
					// InfoList keys its v-for on id || label, and this row has no
					// label — without an id its key would be undefined
					id: 'configuration_instructions',
					key: 'configuration_instructions',
					type: 'link',
					linkText: t('admin_integrations_configuration_instructions'),
					url: documentationUrl.value,
					target: '_blank',
				},
		  ]
		: []),
])

// Utilities
const router = useRouter()
const { t } = useI18n()
const alert = useAlert()

const emit = defineEmits([
	'delete',
	'settingChange',
	'execFullSync',
	'editRoleMappings',
])

// Computed
const connectionInfoItems = computed(() => [
	{
		label: t('admin_integrations_platform'),
		value: props.configuration?.platform || '--',
	},
	{
		label: t('admin_integrations_client_id'),
		value: props.platformConfiguration?.lti13_client_id || '--',
	},
	{
		label: t('admin_integrations_deployment_id'),
		value: props.platformConfiguration?.lti13_deployment_id || '--',
	},
	// {
	// 	label: 'MAX GROUP SIZE',
	// 	value: props.connection?.max_group_size || 'Unknown',
	// },
])

const showFullSync = computed(
	() => !!props.configuration && 'full_sync_available' in props.configuration
)

// full_sync_available is only refreshed by a configure response, so once the
// cooldown elapses the button would sit disabled behind a stale "Sync
// available <past date>" pill until the admin reloads. Tick a clock and derive
// availability from the timestamp instead.
const now = ref(Date.now())
let cooldownTimer = null

const fullSyncAvailable = computed(() => {
	if (props.configuration?.full_sync_available) return true
	const availableAt = props.configuration?.full_sync_available_at
	if (!availableAt) return false
	// moment.utc so a naive timestamp is read as UTC, matching how dateFormat
	// renders the same field in the pill below. new Date() would parse it as
	// local time and unlock the button a UTC offset early.
	const timestamp = moment.utc(availableAt).valueOf()
	return !Number.isNaN(timestamp) && now.value >= timestamp
})

const showRoleMapping = computed(
	() => !!props.configuration && 'role_mappings' in props.configuration
)

const showGroupRemoval = computed(
	() =>
		!!props.configuration && 'remove_group_on_course_end' in props.configuration
)

const documentationUrl = computed(() =>
	getDocumentationUrl(props.configuration?.platform)
)

const showActionsSection = computed(
	() => showFullSync.value || showRoleMapping.value
)

const hasRoleMappings = computed(() =>
	Object.values(props.configuration?.role_mappings || {}).some(
		(names) => Array.isArray(names) && names.length
	)
)

const actionsInfoItems = computed(() => [
	...(showRoleMapping.value
		? [
				{
					key: 'role_mapping',
					label: t('admin_integrations_role_mapping'),
				},
		  ]
		: []),
	...(showFullSync.value
		? [
				{
					key: 'full_sync',
					label: t('admin_integrations_sync_all_resources'),
				},
		  ]
		: []),
])

// Methods
const handleSettingChange = (key, newValue) => {
	emit('settingChange', {
		[key]: newValue,
	})
}

const onSelectAction = (action) => {
	switch (action.id) {
		case 'delete':
			emit('delete')
			break
	}
}

onMounted(() => {
	cooldownTimer = setInterval(() => (now.value = Date.now()), 30000)
})

onBeforeUnmount(() => {
	clearInterval(cooldownTimer)
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.connection-info-container {
	padding-top: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: flex-start;

	.connection-info-item {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		gap: 6px;
		font-family: Avenir-Roman;
		font-size: 14px;
		color: var(--neutral-800);

		.info-list-label {
			font-size: 11px;
			color: var(--neutral-600);
		}
	}
}

.info-list-label {
	text-transform: uppercase;
}

// Settings styling
// Deliberately not a flex row: these labels wrap at the sidebar's 240px, and a
// flex icon would centre itself against the whole wrapped block instead of
// trailing the last word. Inline flow keeps it next to the text either way.
.label-tip {
	label {
		// _common.scss blockifies every bare <label>, which would drop the icon
		// onto its own line now that this isn't a flex row
		display: inline;
		color: var(--neutral-600);
		font-family: Avenir-Roman;
		font-size: 14px;
	}

	.help-icon {
		color: var(--neutral-600);
		font-size: 12px;
		cursor: help;
		margin-left: 6px;
		vertical-align: middle;
	}
}

.settings-toggle-switch {
	font-size: 24px;
}

// The sidebar column is a fixed 240px (DetailsLayout), so the stepper holds its
// width and the label wraps into whatever is left
.settings-stepper {
	flex-shrink: 0;
}

:deep(.info-list-item:has(.number-stepper)) {
	align-items: center;
	gap: 8px;
}

// Override InfoList spacing for toggles
:deep(.info-list-item) {
	align-items: center; // Center align the toggle with the label
	min-height: 32px; // Ensure consistent height
}

.role-mapping-button {
	padding: 0;
	gap: 6px;
	height: auto;

	.pronto-icon {
		font-size: 12px;
	}
}

.sync-all-button {
	padding: 0;
	gap: 6px;
	height: auto;

	// link Buttons render as <a>, so disabled is a class, not the attribute
	&.disabled {
		color: var(--blue-200);
		opacity: 1;
	}

	.pronto-icon {
		font-size: 12px;

		&.spin {
			color: var(--blue-300);
		}
	}
}

// Matches the Figma sync-countdown chip: 28px tall, 14px radius, single line;
// full width so its edges align with the action row above
.sync-available-pill {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	height: 28px;
	width: 100%;
	padding: 0 10px;
	border: 1px solid var(--neutral-200);
	border-radius: 14px;
	background: var(--neutral-0);
	font-family: Avenir-Roman;
	font-size: 12px;
	line-height: 16px;
	color: var(--neutral-600);
	white-space: nowrap;
	overflow: hidden;

	// The sidebar column is a fixed 240px (DetailsLayout) and the es/pt labels
	// run well past it. Ellipsis has to sit on the flex items, not the flex
	// container, and the label truncates before the date because the date is
	// the information. The title attribute keeps the full timestamp.
	> span {
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}

	strong {
		font-family: Avenir-Heavy;
		font-weight: normal;
		flex-shrink: 0;
	}
}

.settings-link {
	padding: 0;
	&.full-width {
		width: 100%;
	}
	.pronto-icon {
		font-size: 12px;
	}
}

// Override InfoList item styling for link items
:deep(.info-list-item:has(.full-width)) {
	.info-list-label {
		display: none; // Hide the label column for link items
	}
	.info-list-value {
		flex: 1; // Let the value take full width
	}
}
</style>
